/**
 * Provisioning an organisation and nominating its first administrator.
 *
 * @module components/platform/CreateTenantForm
 */

import React, { useEffect, useRef, useState } from "react";

import { ONBOARDING_LABELS, validateTenantForm } from "@/components/platform/tenant-directory";
import {
  Button,
  ErrorAlert,
  FormField,
  LinkButton,
  MutedText,
  Panel,
  SuccessAlert,
  TextField,
} from "@/components/ui";
import type { CreateTenantInput, TenantOnboardingSummary } from "@/platform";
import { focusFirstProblem, problemsByField, withoutProblem } from "@/utils/field-problems";

/** Empty form state, also used to reset after a successful creation. */
const EMPTY: CreateTenantInput = { name: "", slug: "", adminEmail: "", adminDisplayName: "" };

export interface CreateTenantRequest {
  form: CreateTenantInput;
  /** Comma- or newline-separated. Other addresses that reach the same person. */
  alternateEmails: string;
}

export interface CreateTenantFormProps {
  pending: boolean;
  /** What the last attempt refused with, from the server. */
  error: string | null;
  created: TenantOnboardingSummary | null;
  /** Resolves true when the tenant exists, and the form then clears. */
  onCreate: (request: CreateTenantRequest) => Promise<boolean>;
  /** Drops a stale server refusal as soon as a field is touched. */
  onErrorCleared: () => void;
}

/** The four the form types into; `adminAlternateEmails` is parsed separately. */
type TenantTextField = "name" | "slug" | "adminEmail" | "adminDisplayName";

const FIELDS: readonly {
  key: TenantTextField;
  label: string;
  /** Carries the field's example: a placeholder reads as a value already filled in. */
  help: string;
  type?: string;
  required?: boolean;
}[] = [
  {
    key: "name",
    label: "Organisation name",
    help: "Use the official organisation name that users will see, for example Indigo Plateau Institute of Technology.",
    required: true,
  },
  {
    key: "slug",
    label: "Slug",
    help: "Lowercase letters, digits, and hyphens, for example indigo-plateau. Words the application uses, such as admin or api, are reserved.",
    required: true,
  },
  {
    key: "adminEmail",
    label: "Administrator email",
    help: "This person completes first sign-in and links the organisation to their Microsoft directory. For example, registrar@indigo-plateau.edu.",
    type: "email",
    required: true,
  },
  {
    key: "adminDisplayName",
    label: "Administrator name",
    help: "Optional display name shown in tenant membership and audit records, for example Dr Lance Wataru.",
  },
];

/** The testid each field has carried since the console was a single file. */
const FIELD_TESTIDS: Record<TenantTextField, string> = {
  name: "create-tenant-name",
  slug: "create-tenant-slug",
  adminEmail: "create-tenant-admin-email",
  adminDisplayName: "create-tenant-admin-name",
};

/**
 * @example
 * ```tsx
 * <CreateTenantForm pending={pending} error={error} created={created} onCreate={create} … />
 * ```
 */
export function CreateTenantForm({
  pending,
  error,
  created,
  onCreate,
  onErrorCleared,
}: CreateTenantFormProps): React.ReactElement {
  const [form, setForm] = useState<CreateTenantInput>(EMPTY);
  const [alternateEmails, setAlternateEmails] = useState("");
  // The form's own refusals, from the rules the server also applies. Kept apart
  // from the caller's so typing clears the one typing can fix.
  const [problems, setProblems] = useState<Partial<Record<TenantTextField, string>>>({});
  const outcome = useRef<HTMLDivElement>(null);

  // Focus follows the outcome: the fields it came from have just emptied.
  useEffect(() => {
    if (created) {
      outcome.current?.focus();
    }
  }, [created]);

  const edit =
    (key: TenantTextField) =>
    (value: string): void => {
      setForm((current) => ({ ...current, [key]: value }));
      setProblems((current) => withoutProblem(current, key));
      if (error) {
        onErrorCleared();
      }
    };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (pending) {
      return;
    }
    const found = validateTenantForm(form);
    setProblems(problemsByField(found));
    if (found.length > 0) {
      focusFirstProblem(found, (field) => `new-tenant-${field}`);
      return;
    }
    const sent = form;
    const sentAlternates = alternateEmails;
    onCreate({ form, alternateEmails }).then((made) => {
      // Only what was sent is cleared: anything typed while it was in flight
      // is the next draft, not this one.
      if (made) {
        setForm((current) => (current === sent ? EMPTY : current));
        setAlternateEmails((current) => (current === sentAlternates ? "" : current));
      }
    });
  };

  return (
    <Panel aria-label="Create tenant">
      <MutedText className="mb-6">
        Create a tenant from its name and its first administrator&apos;s email. The administrator
        signs in with their own organisation&apos;s Microsoft account at the usual address, and that
        first sign-in links the organisation&apos;s SocratIQ tenant to their Microsoft Entra
        directory.
      </MutedText>
      {/* `noValidate`: the browser's own bubble would answer an empty required
          field first, and it neither names the rule nor stays on screen. */}
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <div className="grid gap-4 md:grid-cols-2">
          {FIELDS.map((entry) => (
            <FormField
              key={entry.key}
              label={entry.label}
              htmlFor={`new-tenant-${entry.key}`}
              helpText={entry.help}
              required={entry.required}
              error={problems[entry.key]}
            >
              <TextField
                id={`new-tenant-${entry.key}`}
                type={entry.type}
                // On the control as well as the label: the asterisk beside the
                // label is the only thing that says so otherwise, and it is
                // not in the field's accessible state.
                required={entry.required}
                value={form[entry.key] ?? ""}
                onChange={edit(entry.key)}
                data-testid={FIELD_TESTIDS[entry.key]}
              />
            </FormField>
          ))}
          <FormField
            label="Alternate emails"
            htmlFor="new-tenant-admin-alternate-emails"
            helpText="Optional. Other addresses that reach the same administrator, comma-separated, for example lance@indigo-plateau.edu, admin@indigo-plateau.edu. A first sign-in from any of these matches this same invitation."
          >
            <TextField
              id="new-tenant-admin-alternate-emails"
              value={alternateEmails}
              onChange={setAlternateEmails}
              data-testid="create-tenant-admin-alternate-emails"
            />
          </FormField>
        </div>

        {error ? <ErrorAlert data-testid="create-tenant-error">{error}</ErrorAlert> : null}

        {/* One polite region, focused when it fills, so the outcome is both
            announced and where the reader lands. */}
        <div
          ref={outcome}
          role="status"
          tabIndex={-1}
          className="outline-none empty:hidden"
          data-testid="create-tenant-outcome"
        >
          {created ? (
            <SuccessAlert role="none" data-testid="create-tenant-success">
              <span className="flex flex-wrap items-center gap-3">
                <span>
                  {created.name} is ready ({ONBOARDING_LABELS[created.onboardingStatus]}).{" "}
                  {created.invitationEmailStatus === undefined
                    ? `No invitation email was queued, so tell ${created.nominatedAdminEmail ?? "the administrator"} directly. They sign in with their organisation's Microsoft account.`
                    : `An invitation email is queued for ${created.nominatedAdminEmail ?? "the administrator"}. They sign in with their organisation's Microsoft account.`}
                </span>
                <LinkButton
                  to={`/admin/manage/${created.id}`}
                  size="sm"
                  variant="outline"
                  data-testid="create-tenant-open"
                >
                  Open {created.name}
                </LinkButton>
              </span>
            </SuccessAlert>
          ) : null}
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={pending}
            aria-disabled={pending || undefined}
            data-testid="create-tenant-submit"
          >
            {pending ? "Creating…" : "Create tenant"}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
